import { ref, computed, unref, watch } from 'vue';
import Network from 'lib/api/network';

let tempIdCounter = 0;
const makeEmptyTag = () => {
  tempIdCounter += 1;
  const id = `temp-${tempIdCounter}`;
  return {
    id,
    hashtag: null,
    emoji: null,
    description: null,
  };
};

const tags = ref([]);
const dirtyTagsIds = ref(new Set());
const pending = ref(new Set());
const examples = ref(new Map());
export const committedTags = ref([]);

export function isTagCommitted(id) {
  // i.e. the ID is a temporary ID
  const idIsTemporary = typeof id === 'string' && id.startsWith('temp-');
  return !idIsTemporary;
}

export function useSharedState() {
  return { tags, pending, dirtyTagsIds };
}

export function useUsageExamples(tagId) {
  return computed(() => examples.value.get(tagId));
}

export function initCommittedTags(initTags) {
  committedTags.value = initTags;
}

function watchTagOnceForDirtiness(tag) {
  const stop = watch(
    tag,
    () => {
      dirtyTagsIds.value.add(tag.id);

      // Once a tag's been marked as dirty, we no longer need to watch it
      stop();
    },
    // We can use once: true in Vue 3.4+
    // { once: true },
  );
}

export function setInitialTags(initialTags) {
  const withEmptyTag = [...initialTags, makeEmptyTag()];
  tags.value = withEmptyTag;

  for (const tag of tags.value) {
    watchTagOnceForDirtiness(tag);
  }
}

function removeTag(tagToRemove) {
  tags.value = tags.value.filter((tag) => tag.id !== tagToRemove.id);
}

function findTag(id) {
  return unref(tags).find((x) => x.id === id);
}

export function addTag() {
  tags.value.push(makeEmptyTag());
  watchTagOnceForDirtiness(tags.value[tags.value.length - 1]);
}

export function commitTag({
  id,
  confirm = false,
  action,
  onComplete,
  onSuccess,
  onNeedConfirmation,
  onError,
}) {
  const tag = findTag(id);

  if (!isTagCommitted(id) && action === 'delete') {
    dirtyTagsIds.value.delete(id);
    const empty = makeEmptyTag();
    for (const field in empty) {
      tag[field] = empty[field];
    }
    watchTagOnceForDirtiness(tag);
    return;
  }

  const callbacks = {
    success: (response) => {
      switch (action) {
        case 'create':
          // delete the temp id
          dirtyTagsIds.value.delete(id);

          // update the local tag state to that from the server
          for (const prop in response) {
            tag[prop] = response[prop];
          }

          watchTagOnceForDirtiness(tag);
          break;

        case 'update':
          dirtyTagsIds.value.delete(response.id);
          watchTagOnceForDirtiness(findTag(response.id));
          break;

        case 'delete':
          removeTag({ id });
          if (tags.value.length === 0) {
            addTag();
          }
          break;

        default:
      }

      committedTags.value = tags.value.filter((t) => isTagCommitted(t.id));
      onSuccess?.(response);
    },
    error: (error) => {
      if (error?.responseJSON?.error_code === 'TAG_HAS_ASSOCIATED_SHOUTOUTS') {
        onNeedConfirmation?.(id);
      } else {
        onError?.(error?.responseJSON?.error_code);
      }
    },
    complete: () => {
      pending.value.delete(id);
      onComplete?.(id);
    },
  };
  pending.value.add(id);

  switch (action) {
    case 'create':
      Network.post('/shoutouts/tags', { ...tag, id: undefined }, callbacks);
      break;

    case 'update':
      Network.put(`/shoutouts/tags/${id}`, { ...tag, confirm }, callbacks);
      break;

    case 'delete':
      Network.delete(`/shoutouts/tags/${id}`, { confirm }, callbacks);
      break;

    default:
  }
}

export function fetchUsageExamples(tagId) {
  if (examples.value.get(tagId)) return;

  const callbacks = {
    success: (data) => {
      examples.value.set(tagId, data);
    },
    error: () => {},
    complete: () => {},
  };
  Network.get(`/shoutouts/tags/${tagId}/usage_examples`, callbacks);
}
